import { useState } from "react";
import { confirmResetPassword } from "aws-amplify/auth";
import { TextInput, PasswordInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { zod4Resolver } from "mantine-form-zod-resolver";
import { z } from "zod";
import { usePostHog } from "posthog-js/react";
import { AuthEvent } from "../../../data/auth-events";
import { passwordWithRequirementsSchema } from "./validation-schemas";
import PasswordRequirements from "./PasswordRequirements";
import styles from "./claimable-authenticator-v2.module.scss";

const resetPasswordSchema = z
  .object({
    code: z.string().min(1, { message: "Please enter the confirmation code" }),
    password: passwordWithRequirementsSchema,
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

type ResetPasswordFormValues = z.infer<typeof resetPasswordSchema>;

type ResetPasswordFormProps = {
  email: string;
  onPasswordReset: () => void;
  onBackToSignIn: () => void;
};

const ResetPasswordForm = ({
  email,
  onPasswordReset,
  onBackToSignIn,
}: ResetPasswordFormProps) => {
  const posthog = usePostHog();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const form = useForm<ResetPasswordFormValues>({
    initialValues: {
      code: "",
      password: "",
      confirmPassword: "",
    },
    validate: zod4Resolver(resetPasswordSchema),
  });

  const handleSubmit = async (values: ResetPasswordFormValues) => {
    if (isSubmitting) return;

    posthog?.capture(AuthEvent.ResetPasswordSubmit);
    setIsSubmitting(true);
    setError(null);

    const trimmedEmail = email.trim();
    const trimmedCode = values.code.trim();

    try {
      await confirmResetPassword({
        username: trimmedEmail,
        confirmationCode: trimmedCode,
        newPassword: values.password,
      });

      posthog?.capture(AuthEvent.ResetPasswordSuccess);
      onPasswordReset();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "An error occurred";
      posthog?.capture(AuthEvent.ResetPasswordError, { error: errorMessage });
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.resetPasswordForm}>
      <img
        src="/images/logo/logo_black_large.svg"
        alt="Claimable"
        className={styles.formLogo}
      />
      <h1 className={styles.formTitle}>{"Reset Password"}</h1>

      <p className={styles.formSubtitle}>
        {"We sent a code to "}
        <strong>{email}</strong>
        {". Enter it below with your new password."}
      </p>

      <form onSubmit={form.onSubmit(handleSubmit)} className={styles.form}>
        <TextInput
          label="Confirmation Code"
          placeholder="Enter your code"
          size="lg"
          value={form.values.code}
          onChange={(e) => form.setFieldValue("code", e.currentTarget.value)}
          onBlur={() => form.validateField("code")}
          error={form.errors.code}
          classNames={{
            input: styles.mantineInput,
            label: styles.inputLabel,
          }}
        />

        <div className={styles.inputGroup}>
          <PasswordInput
            label="New Password"
            placeholder="Enter new password"
            size="lg"
            value={form.values.password}
            onChange={(e) =>
              form.setFieldValue("password", e.currentTarget.value)
            }
            error={
              form.isTouched("password") && form.errors.password ? true : false
            }
            classNames={{
              input: styles.mantineInput,
              label: styles.inputLabel,
            }}
          />
          <PasswordRequirements password={form.values.password} />
        </div>

        <PasswordInput
          label="Confirm Password"
          placeholder="Confirm new password"
          size="lg"
          value={form.values.confirmPassword}
          onChange={(e) =>
            form.setFieldValue("confirmPassword", e.currentTarget.value)
          }
          onBlur={() => form.validateField("confirmPassword")}
          error={form.errors.confirmPassword}
          classNames={{
            input: styles.mantineInput,
            label: styles.inputLabel,
          }}
        />

        {error && <p className={styles.formError}>{error}</p>}

        <button
          type="submit"
          className={styles.primaryButton}
          disabled={isSubmitting}
        >
          {isSubmitting ? "RESETTING..." : "RESET PASSWORD"}
        </button>
      </form>

      <div className={styles.switchAuthMode}>
        <button
          type="button"
          onClick={onBackToSignIn}
          className={styles.switchLink}
        >
          {"Back to Sign In"}
        </button>
      </div>
    </div>
  );
};

export default ResetPasswordForm;
